import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { useInboxAlerts } from '@/features/alerts/use-inbox-alerts';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { INBOX_CONVERSATION_PARAM } from '@/routes/paths';
import { ConversationList } from './ConversationList';
import { ConversationPanel } from './ConversationPanel';
import { useConversations } from './use-conversations';
import {
  CONVERSACIONES_POR_PAGINA,
  EMPTY_INBOX_FILTROS,
  conversationFiltrosFrom,
  type InboxFiltros,
} from './inbox-format';

/**
 * Paso 5 del plan (`docs/FRONTEND_PLAN.md` §4): Inbox (`/conversations`, módulo 5).
 *
 * Dos columnas que se llevan por separado:
 *  - **izquierda**, la bandeja (`ConversationList`): filtros en el servidor, búsqueda con retardo y
 *    paginación por `limite`/`offset`. Se refresca sola cada `INBOX_REFRESH_MS` porque el inbox no
 *    lo mueve el usuario: llegan mensajes y las conversaciones se reordenan solas;
 *  - **derecha**, la conversación abierta (`ConversationPanel`): historial por cursor, pedidos del
 *    contacto y el interruptor del bot (handoff, solo ADMIN).
 *
 * Esta página solo es el PEGAMENTO: tiene la selección, los filtros y el `offset`, y le pasa a cada
 * columna lo suyo. Todo el formato está en `inbox-format.ts` (funciones puras, probadas en el smoke)
 * y cada columna pide sus propios datos.
 *
 * Decisiones que se ven en el código:
 *  - cambiar CUALQUIER filtro vuelve a la página 0 (`handleFiltrosChange`): el offset 60 de un filtro
 *    no significa nada con otro, y casi siempre esa página quedaría vacía;
 *  - la conversación abierta se guarda en la URL (`?conversacion=<id>`): recargar o compartir el
 *    enlace abre la misma conversación, y el panel se monta/desmonta solo al cambiar el id;
 *  - la altura de la pantalla es la del viewport en escritorio (`lg:h-[calc(100vh-9rem)]`): el panel
 *    se queda quieto y lo que hace scroll son la bandeja y el historial, no la página entera;
 *  - la MISMA pestaña alimenta los avisos de «mensajes nuevos» (`useInboxAlerts`): marca vista la
 *    conversación abierta mientras se lee, enseña en la tarjeta de la bandeja cuántas tienen mensajes
 *    sin ver (con «Marcar como visto») y pone el punto azul en cada fila. La consulta de avisos son
 *    los filtros por defecto, así que con la bandeja sin filtrar comparte petición con el listado;
 *    con filtros puestos son dos lecturas (una por cada consulta).
 */

export function InboxPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const [searchParams, setSearchParams] = useSearchParams();

  /** Filtros de la bandeja tal cual los edita el usuario (ver `InboxFiltros`). */
  const [filtros, setFiltros] = useState<InboxFiltros>(EMPTY_INBOX_FILTROS);
  const [offset, setOffset] = useState(0);

  const conversationId = searchParams.get(INBOX_CONVERSATION_PARAM);

  // El contrato (`ConversationFiltros`) se arma con una función pura y memoizado: es la misma
  // referencia mientras no cambien los filtros, así la query key de TanStack no se regenera de
  // gratis en cada render.
  const consulta = useMemo(() => conversationFiltrosFrom(filtros, offset), [filtros, offset]);
  const conversacionesQuery = useConversations(tenantId, consulta);

  // Avisos: la conversación abierta se marca vista sola, el aviso vive en la tarjeta de la bandeja y
  // las filas con mensajes sin ver llevan punto. El hook se monta también en el shell (`AppLayout`)
  // y comparte consulta y estado.
  const { alertas, marcarTodasVistas } = useInboxAlerts({ conversacionAbierta: conversationId });

  const conversaciones = conversacionesQuery.data?.conversaciones ?? [];
  const total = conversacionesQuery.data?.total ?? 0;

  /** Ids de las conversaciones con mensajes sin ver que están en la página cargada. */
  const nuevasIds = useMemo(
    () => new Set(alertas.nuevas.map((alerta) => alerta.conversacion.id)),
    [alertas.nuevas],
  );

  /**
   * La fila de la bandeja de la conversación abierta, para que la cabecera se pinte al instante.
   * Puede ser `null` sin que haya error: un enlace abierto desde otra página o un cambio de filtro
   * pueden dejar fuera del listado la conversación que está abierta (el panel la carga igual por su
   * id, con `useConversation`).
   */
  const resumenAbierto = conversaciones.find((fila) => fila.id === conversationId) ?? null;

  const handleFiltrosChange = useCallback((cambios: Partial<InboxFiltros>) => {
    setFiltros((actual) => ({ ...actual, ...cambios }));
    setOffset(0);
  }, []);

  const handleClearFiltros = useCallback(() => {
    setFiltros(EMPTY_INBOX_FILTROS);
    setOffset(0);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      // `replace` a propósito: abrir conversaciones no debería llenar el historial del navegador de
      // una entrada por clic, que es lo que hace que el botón "atrás" deje de servir.
      setSearchParams(
        (actuales) => {
          const siguiente = new URLSearchParams(actuales);
          siguiente.set(INBOX_CONVERSATION_PARAM, id);
          return siguiente;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * El chat abierto se borró: se saca de la URL y el panel derecho vuelve al estado vacío. Sin esto
   * la página se queda mirando un `conversationId` que ya no existe y el detalle pide un 404 en
   * cada refresco (el inbox refresca solo).
   */
  const handleConversationDeleted = useCallback(() => {
    setSearchParams(
      (actuales) => {
        const siguiente = new URLSearchParams(actuales);
        siguiente.delete(INBOX_CONVERSATION_PARAM);
        return siguiente;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  if (!tenantId) {
    return (
      <EmptyState
        icon={<Inbox size={22} />}
        title="No hay un negocio activo"
        description="Elige un negocio en el selector de la izquierda para abrir su bandeja de conversaciones."
      />
    );
  }

  return (
    <div className="grid gap-4 lg:h-[calc(100vh-9rem)] lg:min-h-[30rem] lg:grid-cols-[minmax(19rem,25rem)_1fr] lg:items-stretch">
      {/* `min-h-0` en las dos columnas: sin él, el contenido del scroll estira la fila del grid y la
          página entera hace scroll en vez del panel.
          `min-h-[30rem]` es el suelo del panel: en una ventana baja (`100vh - 9rem` ≈ 480 px en un
          portátil de 720 px de alto) el chat se quedaba con dos dedos de alto y parecía que la
          conversación no tenía mensajes. Con el suelo, cuando no cabe manda el mínimo y la página
          hace un poco de scroll; cuando sí cabe, el alto de la ventana manda igual que antes. */}
      <div className="flex min-h-0 flex-col">
        <ConversationList
          conversaciones={conversaciones}
          total={total}
          offset={offset}
          limite={CONVERSACIONES_POR_PAGINA}
          selectedId={conversationId}
          nuevasIds={nuevasIds}
          onMarcarVistas={marcarTodasVistas}
          onSelect={handleSelect}
          filtros={filtros}
          onFiltrosChange={handleFiltrosChange}
          onClearFiltros={handleClearFiltros}
          onOffsetChange={setOffset}
          onRefresh={() => void conversacionesQuery.refetch()}
          refreshing={conversacionesQuery.isFetching}
          isPending={conversacionesQuery.isPending}
          error={conversacionesQuery.error}
        />
      </div>

      <div className="flex min-h-0 flex-col">
        <ConversationPanel
          tenantId={tenantId}
          conversationId={conversationId}
          resumen={resumenAbierto}
          canEdit={canEdit}
          onDeleted={handleConversationDeleted}
        />
      </div>
    </div>
  );
}
